import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ConstructAPI } from 'src/app/API/constructAPI';
import { RestApiService } from 'src/app/API/restapi';
import { ALERT_TYPE } from 'src/app/constants';
import { LoanRequestMessageComponent } from 'src/app/MessageClipboard/loan-request-message/loan-request-message.component';
import { Enums } from 'src/app/Shared/enums';
import { SharedService } from 'src/app/Shared/shared-service.service';
import { CompanyAccess } from 'src/model/companyAccess.model';
import { LoanRequest } from 'src/model/loanRequest.model';
import { LoanGroupRequest } from 'src/model/sharedLoan.model';
import { TransactionRequest } from 'src/model/transactionRequest.model';
import { AdminAccessService } from 'src/services/admin-access.service';
import { LoanRequestService } from 'src/services/loan-request.service';
import { SetService } from 'src/services/set.service';

interface Usage {
  CompanyCode: string,
  CompanyId: number,
  LastInsertedDate: Date,
  LatestDate: Date,
  Group: number
}

@Component({
    selector: 'app-dashboard-admin',
    templateUrl: './dashboard-admin.component.html',
    styleUrls: ['./dashboard-admin.component.scss'],
    standalone: false
})
export class DashboardAdminComponent implements OnInit {
  private adminAccess = inject(AdminAccessService);
  private router = inject(Router);
  private restApi = inject(RestApiService);
  private constructApi = inject(ConstructAPI);
  private cdr = inject(ChangeDetectorRef);
  protected sharedService = inject(SharedService);
  private enums = inject(Enums);
  private setService = inject(SetService);
  private loanRequestService = inject(LoanRequestService);

  loanRequestList: LoanRequest[] = [];
  approvedWithoutloanRequestList: LoanRequest[] = [];
  wholeApprovedLoanRequestList: LoanRequest[] = [];
  wholeLoanRequestList: LoanRequest[] = [];
  normalLoanRequestList: LoanRequest[] = [];
  wholeNormalLoanRequestList: LoanRequest[] = [];
  sharedLoanRequestList: LoanGroupRequest[] = [];
  wholeSharedLoanRequestList: LoanGroupRequest[] = [];

  transactionRequestList: TransactionRequest[] = [];

  usageList: Usage[] = [];

  searchText = "";
  searchApprovedText = "";
  searchNormalText = "";

  setCountToday = 0;
  setCountPending = 0;
  setCountDone = 0;
  setDonePercentage = 0;
  setCountTotalPending = 0;
  img = '';

  prevSetDate: Date;

  clipboardCompareMessage = "";
  compareRequest: LoanRequest;

  companyAccess: Record<string, CompanyAccess[]> = {};

  //filter options
  isFirstPriority = false;
  isClashedTickets = false;
  //filter used
  disallowedClashTicketIds: number[] = [];
  clashedTickets: LoanRequest[] = [];

  async ngOnInit() {
    if (this.sharedService.isAdmin()) this.getTodayReceiptCount();
    await this.getAllLoanRequests();

    //check all clashed tickets first
    this.clashedTickets = this.checkClashedForPendingTickets();
    //if admin login, default filter = all tickets they need to process
    if (this.sharedService.isAdmin()) {
      //first priority filter TURNED ON
      this.isFirstPriority = true;

      //check if the clashed tickets contained their priority or not, if not NO NEED clashed filter
      const firstPriorityClashed = this.clashedTickets.filter(t => t.Priority == this.enums.FIRST_PRIORITY)
      if (firstPriorityClashed.length > 0)
        this.isClashedTickets = true;
    }

    this.filterTickets();

    //get other infos of dashboard
    this.getAllUsageStats();
    this.getAllAssignedTransactionList();
    if (this.sharedService.isAdmin() || this.sharedService.isBossAccount()) this.getUserCompanyAccess();
  }

  getAllUsageStats() {
    const xhttp = this.restApi.getRequest(this.constructApi.getAllUsageStats(false))
    xhttp.onreadystatechange = () => {
      if (xhttp.readyState == 4 && xhttp.status == 200) {
        const jsonArray = JSON.parse((xhttp.responseText));
        jsonArray.forEach(json => {
          const usage = { CompanyCode: json.companyCode, CompanyId: json.companyId, LastInsertedDate: json.lastInserted, LatestDate: json.latestDate, Group: json.group };

          const timeDiff = new Date().getTime() - new Date(usage.LatestDate).getTime();
          const dateDiff = timeDiff / (1000 * 3600 * 24);

          // if this branch never do account for more than 5 days then only show on dashboard
          if (dateDiff > 5 || usage.LatestDate == undefined)
            this.usageList.push(usage);
        });

        this.usageList.sort((a, b) => (a.LatestDate > b.LatestDate) ? 1 : -1);
      }
    };
  }

  async getAllLoanRequests() {
    const loanList = await this.loanRequestService.getAllLoanApprovalRequest({ withLoan: false, approved: true, includeUnprocessed: true, authorized: true });

    this.normalLoanRequestList = loanList.filter(x => (x.LoanInfo.Scheme !== 'EPF' && x.Approved == undefined));
    this.approvedWithoutloanRequestList = loanList.filter(x => (x.LoanInfo.Scheme == 'EPF' && x.Approved !== undefined));
    this.loanRequestList = loanList.filter(x => x.LoanInfo.Scheme == 'EPF' && x.Approved == undefined && x.LoanGroupId == undefined);

    this.wholeNormalLoanRequestList = [...this.normalLoanRequestList];
    this.wholeApprovedLoanRequestList = [...this.approvedWithoutloanRequestList];
    this.wholeLoanRequestList = [...this.loanRequestList];

    const sharedLoanList = loanList.filter(x => x.LoanInfo.Scheme == 'EPF' && x.Approved == undefined && x.LoanGroupId !== undefined);
    const loanGroupList = this.sharedService.groupBy(sharedLoanList, 'LoanGroupId');
    this.sharedLoanRequestList = Object.keys(loanGroupList).map<LoanGroupRequest>(key => ({
      loanGroup: Number(key),
      requestList: loanGroupList[key]
    }));
    this.wholeSharedLoanRequestList = [...this.sharedLoanRequestList];

    this.cdr.detectChanges();

    this.loanRequestList.sort((a, b) => (a.Priority > b.Priority || a.UpdatedOn > b.UpdatedOn) ? 1 : -1);
    this.wholeLoanRequestList.sort((a, b) => (a.Priority > b.Priority || a.UpdatedOn > b.UpdatedOn) ? 1 : -1);

    this.approvedWithoutloanRequestList.sort((a, b) => (a.Priority > b.Priority || a.UpdatedOn > b.UpdatedOn) ? 1 : -1);
    this.wholeApprovedLoanRequestList.sort((a, b) => (a.Priority > b.Priority || a.UpdatedOn > b.UpdatedOn) ? 1 : -1);

    this.normalLoanRequestList.sort((a, b) => (a.Priority > b.Priority || a.UpdatedOn > b.UpdatedOn || a.LoanInfo.Scheme != "HUTANG") ? 1 : -1);
    this.wholeNormalLoanRequestList.sort((a, b) => (a.Priority > b.Priority || a.UpdatedOn > b.UpdatedOn || a.LoanInfo.Scheme != "HUTANG") ? 1 : -1);
  }

  applyFilter() {
    if (this.searchText == "") {
      this.loanRequestList = this.wholeLoanRequestList;
      this.sharedLoanRequestList = this.wholeSharedLoanRequestList;
    } else {
      this.loanRequestList = this.wholeLoanRequestList.filter(request => request.ID == Number(this.searchText));
      this.sharedLoanRequestList = this.wholeSharedLoanRequestList.filter(group => group.requestList.find(request => request.ID == Number(this.searchText)));
    }
  }

  filterTickets() {
    const matchFilter = (r: LoanRequest): boolean => {
      const clashedTicketIds = this.clashedTickets.map(r => r.ID);
      if (this.isClashedTickets && !clashedTicketIds.includes(r.ID)) return false;

      //first priority filter: only show if fulfilling both first priority and isMain
      //definition of isMain = sharedprofileid = profileid
      if (this.isFirstPriority)
        if (r.Priority != this.enums.FIRST_PRIORITY) return false;
        else if (r.SharedProfileId != undefined && r.SharedProfileId != r.ProfileId) return false;

      if (this.searchText != "" && r.ID != Number(this.searchText)) return false;

      return true;
    }

    this.loanRequestList = this.wholeLoanRequestList.filter(matchFilter);
    this.sharedLoanRequestList = this.wholeSharedLoanRequestList.filter(g => g.requestList.some(matchFilter))
  }

  getClashedTickets(requestList: LoanRequest[]) {
    const grouped: Record<number, LoanRequest[]> = {};

    // Group tickets by customerId
    for (const t of requestList) {
      if (!grouped[t.CustomerIC]) {
        grouped[t.CustomerIC] = [];
      }
      grouped[t.CustomerIC].push(t);
    }

    // Keep only groups with conflicts (more than one ticket)
    const clashedGroups: Record<number, LoanRequest[]> = {};

    let clashedTickets: LoanRequest[] = [];
    const disallowedProcessList: number[] = [];

    for (const [customerIc, tickets] of Object.entries(grouped)) {
      if (tickets.length > 1) {
        //sort the clashed tickets by RequestOn, earliest to latest
        const sortedTickets = tickets.sort((a, b) => new Date(a.RequestOn).getTime() - new Date(b.RequestOn).getTime());
        clashedGroups[+customerIc] = sortedTickets;
        clashedTickets = clashedTickets.concat([...sortedTickets]);

        //splice the earliest ticket in the list and others add into disallowed list
        sortedTickets.splice(1).forEach(t => disallowedProcessList.push(t.ID));
      }
    }

    return { clashedTickets, disallowedProcessList };
  }

  checkClashedForPendingTickets() {
    this.disallowedClashTicketIds = [];

    //list should combine both non shared and shared loan request list (for shared list only consider main request)
    //for shared request list, do an extra sorting to make sure only consider first main request (to avoid either if duplicated tickets happened in same loan group)
    const list = [...this.wholeLoanRequestList,
    ...this.wholeSharedLoanRequestList.flatMap(r => r.requestList.filter(l => l.SharedProfileId == undefined || l.SharedProfileId == l.ProfileId).sort((a, b) =>
      a.ID - b.ID)[0])];

    const { clashedTickets, disallowedProcessList } = this.getClashedTickets(list);

    this.disallowedClashTicketIds = disallowedProcessList;

    return clashedTickets;
  }

  checkDisallowProcessGroup(groupRequest: LoanGroupRequest) {
    return groupRequest.requestList.some(t => this.disallowedClashTicketIds.includes(t.ID));
  }

  applySearchNormalFilter() {
    if (this.searchNormalText == "")

      this.normalLoanRequestList = this.wholeNormalLoanRequestList;
    else
      this.normalLoanRequestList = this.wholeNormalLoanRequestList.filter(request => request.CustomerIC.toUpperCase().includes(this.searchNormalText.toUpperCase()) || request.CustomerName.toUpperCase().includes(this.searchNormalText.toUpperCase()));
  }

  applyApprovedFilter() {
    if (this.searchApprovedText == "")
      this.approvedWithoutloanRequestList = this.wholeApprovedLoanRequestList;
    else
      this.approvedWithoutloanRequestList = this.wholeApprovedLoanRequestList.filter(request => request.ID == Number(this.searchApprovedText));
  }

  async getTodayReceiptCount() {
    const [allReceipt, pendingReceipt, totalPendingReceipt] = await Promise.all([
      this.setService.getAllTxSetsCount({ checked: true, includePending: true, priority: this.enums.FIRST_PRIORITY, startDate: new Date(), endDate: new Date() }),
      this.setService.getAllTxSetsCount({ pending: true, priority: this.enums.FIRST_PRIORITY, startDate: new Date(), endDate: new Date() }),
      this.setService.getAllTxSetsCount({ checked: true, includePending: true, pending: true, priority: this.enums.FIRST_PRIORITY })
    ]);
    this.setCountToday = allReceipt.reduce((sum, current) => sum + current.count, 0);
    this.setCountPending = pendingReceipt.reduce((sum, current) => sum + current.count, 0);
    this.setCountDone = this.setCountToday - pendingReceipt.reduce((sum, current) => sum + current.count, 0);
    this.setDonePercentage = Math.round(this.setCountDone / this.setCountToday * 100);
    this.setCountTotalPending = totalPendingReceipt.reduce((sum, current) => sum + current.count, 0);

    if (this.setCountToday == 0) {
      this.img = this.sharedService.getImage(this.enums.emptyImages);
      const a = await this.setService.getAllTxSet({ priority: this.enums.FIRST_PRIORITY, limit: 1, sortASC: false, pending: true });
      if (a.length > 0) this.prevSetDate = a[0].CreatedOn;
    } else this.img = (this.setDonePercentage == 100) ? this.sharedService.getImage(this.enums.doneImages) : this.sharedService.getImage(this.enums.pendingImages);
  }

  checkPreviousReceipt() {
    this.router.navigate(['/receiptTodo'], { queryParams: { date: this.prevSetDate.toISOString().split('T')[0] } })
  }

  goToTodayReceipts() {
    this.router.navigate(['/receiptTodo']);
  }

  goToReceipts() {
    this.router.navigate(['/receiptList']);
  }

  goToSettlementInfoCheck() {
    this.router.navigate(['/settlementInfoCheck']);
  }

  goToAccountAuthorizations() {
    this.router.navigate(['/accountAuthorizations']);
  }

  getClashedICTickets(request: LoanRequest) {
    return this.clashedTickets.filter(c => c.CustomerIC == request.CustomerIC && c.LoanGroupId != request.LoanGroupId);
  }

  getAllAssignedTransactionList() {
    //only get trasactionRequest without transaction
    const xhttp = this.restApi.getRequest(this.constructApi.getAllTransactionRequests(false))
    xhttp.onreadystatechange = () => {
      if (xhttp.readyState == 4 && xhttp.status == 200) {
        const jsonArray = JSON.parse((xhttp.responseText));

        jsonArray.forEach(json => {
          const itemsArray = json.items;
          const itemList = [];
          itemsArray.forEach(itemJson => {
            const item = { "amount": itemJson.amount, "type": itemJson.type, "subtype": itemJson.subtype }

            itemList.push(item);
          });

          const transactionRequest = new TransactionRequest(json.id, json.companyCode, json.companyid, json.remark, json.requestedBy, json.requestedOn, itemList);
          this.transactionRequestList.push(transactionRequest);
        });
      }
    };
  }

  clickPaste() {
    navigator.clipboard.readText().then(
      text => this.clipboardCompareMessage = text
    ).catch(error => {
      console.error('Cannot read clipboard text: ', error);
    });
  }

  filterAndCompareMessage() {
    if (this.clipboardCompareMessage != '' && this.clipboardCompareMessage != undefined) {
      const regexp = /\#(\d+)/g;

      //will get ticket number with leading #
      const ticketNo = this.clipboardCompareMessage.match(regexp)

      if (ticketNo != undefined && ticketNo != null) {
        //remove # to get ticket number and do filter
        this.searchText = ticketNo[0].replace('#', "");
        this.filterTickets();

        //search the request in request list
        //if found, add the request into whatsapp clipboard component
        //if not found, search in shared loan request list
        const index = this.wholeLoanRequestList.findIndex(loanRequest => Number(loanRequest.ID) == Number(this.searchText));
        if (index != -1) {
          this.compareRequest = this.wholeLoanRequestList[index];

          var modalRef = this.sharedService.openModal(LoanRequestMessageComponent, "regularModal");
          modalRef.componentInstance.isEPFRequest = true;
          modalRef.componentInstance.requestList = [this.compareRequest];
          modalRef.componentInstance.compareString = this.clipboardCompareMessage;
        } else {
          //if the ticket number is not found in loan request list, find in shared loan request list
          const groupIndex = this.wholeSharedLoanRequestList.findIndex(group => group.requestList.find(request => Number(request.ID) == Number(this.searchText)));

          if (groupIndex != -1) {
            var modalRef = this.sharedService.openModal(LoanRequestMessageComponent, "regularModal");
            modalRef.componentInstance.isEPFRequest = true;

            const list = this.wholeSharedLoanRequestList[groupIndex].requestList.sort((a, b) => ((a.ID > b.ID)) ? -1 : 1);
            modalRef.componentInstance.requestList = list;
            modalRef.componentInstance.compareString = this.clipboardCompareMessage;
            modalRef.componentInstance.loanGroupId = this.wholeSharedLoanRequestList[groupIndex].loanGroup;
          } else {
            this.sharedService.openAlert("Ticket with this ticket number not existed!", ALERT_TYPE.DANGER);
          }
        }
      } else this.sharedService.openAlert("No record found! Please check pasted messsage!", ALERT_TYPE.DANGER);
    } else {
      this.searchText = "";
      this.filterTickets();
    }
  }

  async getUserCompanyAccess() {
    const priorities: number[] = [this.enums.FIRST_PRIORITY, this.enums.SECOND_PRIORITY, this.enums.THIRD_PRIORITY];
    const a = await this.adminAccess.getCompaniesAccess();
    const r = this.sharedService.groupBy(a, 'CompanyId');
    Object.keys(r).forEach(key => {
      this.companyAccess[key] = r[key].filter(a => priorities.includes(a.Priority));
      this.companyAccess[key].sort((a, b) => a.Priority - b.Priority);
    });
  }
}